import React from 'react';
import { translate } from "../../Services/translation";

/**
 * @param {Object} props
 * @param {ProductItem} props.productItem - The price model containing price information
 * @param {boolean} props.showPricesWithVat - Whether to show prices with VAT
 * @param {string} props.innerPac - Inner packaging information
 * @param {string} props.unit - Unit of measurement
 * @returns {React.ReactElement|null}
 */
const ProductPrice = ({
    productItem,
    showPricesWithVat = false,
    innerPac = '',
    unit = ''
}) => {
    if (!productItem.price.price) return null;

    const priceModel = productItem.price;
    let decimalPlaces = parseInt(priceModel.currency.textFormat.replace('C', ''));
    if (isNaN(decimalPlaces)) {
        decimalPlaces = 0;
    }

    /**
     * @param {PriceItem|CampaignPriceItem} priceItem
     * @returns {string}
     */
    const formatPrice = (priceItem) => {
        if (!priceItem || !priceItem.price || !priceModel.currency) return '';

        const { currency } = priceModel;
        const price = showPricesWithVat ? priceItem.priceWithVat : priceItem.price;

        if (typeof price !== 'number') return '';

        try {
            return new Intl.NumberFormat(productItem.culture, {
                style: 'currency',
                currency: currency.id,
                currencyDisplay: 'code',
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces
            }).format(price);
        } catch (err) {
            return price.toString();
        }
    };

    const getInnerPacString = () => {
        if (!innerPac && !unit) return '';
        return `/ ${innerPac ? `${innerPac} ` : ''}${unit || ''}`;
    };

    const getPrice = () => {
        if (priceModel.campaignPrice && priceModel.campaignPrice.price) {
            return showPricesWithVat ? priceModel.campaignPrice.priceWithVat : priceModel.campaignPrice.price;
        }

        if (priceModel.tierPrices && Array.isArray(priceModel.tierPrices) && priceModel.tierPrices.length > 0) {
            const lowestPrice = [...priceModel.price.tierPrices].sort((a, b) => a.price - b.price)[0];
            return showPricesWithVat ? lowestPrice.priceWithVat : lowestPrice.price;
        }

        return showPricesWithVat ? priceModel.price.priceWithVat : priceModel.price.price;
    };

    const price = getPrice();
    if (typeof price !== 'number') return null;

    return (
        <div className="product-price">
            {priceModel.campaignPrice ?
                <>
                    <span className="product__campaign-price">
                        {formatPrice(priceModel.campaignPrice)}
                    </span>
                    <span className="product__old-price">
                        ({formatPrice(priceModel.price)})
                    </span>
                </>
                : 
                <span className="price">
                    {Array.isArray(priceModel.price.tierPrices) && priceModel.price.tierPrices.length > 0 && (
                        <span>{translate('product.pricefrom')} </span>
                    )}
                    {formatPrice(priceModel.price)}
                </span>
            }
            <span className="price-amount">{getInnerPacString()}</span>
            <meta itemProp="price" content={price.toFixed(0)} />
            <meta itemProp="priceCurrency" content={priceModel.currency.id || ''} />
        </div>
    );
};

export default ProductPrice;
